// src/views/bots/flow/palette/NodePalette.tsx
// Draggable node palette for creating bot flow nodes

import React from 'react';
import styles from './NodePalette.module.css';
import logger from '../../../../utils/logger';

export interface NodeTypeDefinition {
  type: string;
  label: string;
  icon: string;
  description: string;
  category: 'start' | 'question';
  color: string;
}

// All available node types for WhatsApp bots
export const NODE_TYPES: NodeTypeDefinition[] = [
  // Start node
  {
    type: 'startNode',
    label: 'Start',
    icon: '🚀',
    description: 'WhatsApp bot entry point — triggered by keywords',
    category: 'start',
    color: '#10b981',
  },

  // Question nodes
  {
    type: 'interactive-button',
    label: 'Button Question',
    icon: '🔘',
    description: '2-3 quick-reply buttons (e.g. Pricing / Catalogue)',
    category: 'question',
    color: '#3b82f6',
  },
  {
    type: 'interactive-list',
    label: 'List Question',
    icon: '📋',
    description: 'Dropdown list for 4+ options (e.g. product categories)',
    category: 'question',
    color: '#8b5cf6',
  },
  {
    type: 'text',
    label: 'Text Input',
    icon: '✍️',
    description: 'Ask for any text response (name, feedback, etc.)',
    category: 'question',
    color: '#06b6d4',
  },
  {
    type: 'email',
    label: 'Email Input',
    icon: '📧',
    description: 'Collect email address (validated)',
    category: 'question',
    color: '#f59e0b',
  },
  {
    type: 'phone',
    label: 'Phone Input',
    icon: '📱',
    description: 'Collect phone number (validated)',
    category: 'question',
    color: '#ec4899',
  },
  {
    type: 'number',
    label: 'Number Input',
    icon: '🔢',
    description: 'Collect a number (quantity, budget, etc.)',
    category: 'question',
    color: '#14b8a6',
  },
  {
    type: 'location',
    label: 'Location',
    icon: '📍',
    description: 'Ask user to share their location',
    category: 'question',
    color: '#f97316',
  },
  {
    type: 'template',
    label: 'Template Message',
    icon: '📄',
    description: 'Pre-approved WhatsApp template',
    category: 'question',
    color: '#6366f1',
  },
];

interface NodePaletteProps {
  onDragStart?: (event: React.DragEvent<HTMLDivElement>, nodeType: string) => void;
}

const NodePalette: React.FC<NodePaletteProps> = ({ onDragStart }) => {
  const handleDragStart = (event: React.DragEvent<HTMLDivElement>, nodeType: string) => {
    logger.info('[NodePalette] Drag started', { nodeType });
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';

    if (onDragStart) {
      onDragStart(event, nodeType);
    }
  };

  // Group nodes by category
  const startNodes = NODE_TYPES.filter(n => n.category === 'start');
  const questionNodes = NODE_TYPES.filter(n => n.category === 'question');

  return (
    <div className={styles.palette} data-tour="node-palette">
      {/* Header */}
      <div className={styles.paletteHeader}>
        <h3 className={styles.paletteTitle}>Node Palette</h3>
        <p className={styles.paletteSubtitle}>Drag & drop nodes to canvas</p>
      </div>

      {/* Start Node Section */}
      <div className={styles.paletteSection}>
        <div className={styles.sectionHeader}>Flow Start</div>
        <div className={styles.nodeList}>
          {startNodes.map((nodeType) => (
            <div
              key={nodeType.type}
              className={styles.paletteNode}
              draggable
              onDragStart={(e) => handleDragStart(e, nodeType.type)}
              style={{ borderLeftColor: nodeType.color }}
            >
              <div className={styles.nodeIcon}>{nodeType.icon}</div>
              <div className={styles.nodeInfo}>
                <div className={styles.nodeLabel}>{nodeType.label}</div>
                <div className={styles.nodeDescription}>{nodeType.description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Question Nodes Section */}
      <div className={styles.paletteSection}>
        <div className={styles.sectionHeader}>Question Types</div>
        <div className={styles.nodeList}>
          {questionNodes.map((nodeType) => (
            <div
              key={nodeType.type}
              className={styles.paletteNode}
              draggable
              onDragStart={(e) => handleDragStart(e, nodeType.type)}
              style={{ borderLeftColor: nodeType.color }}
              data-tour={`node-${nodeType.type}`}
            >
              <div className={styles.nodeIcon}>{nodeType.icon}</div>
              <div className={styles.nodeInfo}>
                <div className={styles.nodeLabel}>{nodeType.label}</div>
                <div className={styles.nodeDescription}>{nodeType.description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NodePalette;
